import { IconQR, IconDining, IconReceipt, IconUsers } from './icons';

const CAPABILITIES = [
  {
    Icon: IconQR,
    title: 'One code per spot',
    body: 'Each QR maps to a specific location — table, counter slot, or parking bay. Scan from any phone and the menu knows exactly where the guest is.',
  },
  {
    Icon: IconDining,
    title: 'Full menu, zero friction',
    body: 'Photos, mods, dietary tags, AI pairings — the complete dining experience in a browser tab. No download, no account, no wait.',
  },
  {
    Icon: IconReceipt,
    title: 'Sub-second to the kitchen',
    body: 'The moment a guest confirms, the ticket appears in the Live Order Hub under the right table. No polling. No missed orders.',
  },
  {
    Icon: IconUsers,
    title: 'Split bills, shared carts',
    body: 'Multiple guests, one table. Everyone orders independently or together — pay separately or split the bill at checkout.',
  },
];

const FLOOR_TABLES = [
  { id: 'T1',  status: 'done',   items: 'Espresso ×2',          section: 'A' },
  { id: 'T2',  status: 'empty',  items: null,                   section: 'A' },
  { id: 'T3',  status: 'active', items: 'Sea bass, House red',  section: 'A' },
  { id: 'T4',  status: 'empty',  items: null,                   section: 'B' },
  { id: 'T5',  status: 'active', items: 'Steak ×2, Cab Sauv',   section: 'B' },
  { id: 'T6',  status: 'new',    items: 'Burrata, Wagyu ×2',    section: 'B' },
  { id: 'T7',  status: 'new',    items: 'Arancini, Negroni ×3', section: 'B' },
  { id: 'T8',  status: 'empty',  items: null,                   section: 'C' },
  { id: 'T9',  status: 'done',   items: 'Tiramisu ×2',          section: 'C' },
  { id: 'T10', status: 'active', items: 'Pasta, Chianti',        section: 'C' },
  { id: 'T11', status: 'empty',  items: null,                   section: 'C' },
  { id: 'T12', status: 'new',    items: 'Risotto ×1, Water',    section: 'C' },
];

export default function TableOrdering() {
  return (
    <section className="tableord" id="tableord">

      {/* Header */}
      <div className="tableord-header">
        <div className="tableord-header-l">
          <div className="kicker reveal">
            <span className="kicker-dot" />
            <span>Every order, live</span>
          </div>
          <h2 className="display tableord-title reveal-words">
            The whole floor,
            <br />
            in <em>real time.</em>
          </h2>
        </div>
        <div className="tableord-header-body">
          <p className="lede reveal">
            A QR at the table, counter, or car window turns any phone into an
            ordering terminal. Guests browse, customise, and confirm in under
            30 seconds — the ticket lands at the kitchen before they put their
            phone down.
          </p>
        </div>
      </div>

      {/* Live floor plan + side feed */}
      <div className="tableord-stage reveal">

        {/* Floor plan */}
        <div className="ts-floor">
          <div className="ts-floor-head">
            <span className="ts-feed-dot ts-feed-dot--live" />
            <span>Floor plan · live view</span>
            <span className="ts-floor-meta">12 tables · 4 active</span>
          </div>
          <div className="ts-floor-grid">
            {FLOOR_TABLES.map((t) => (
              <div className={`ts-table ts-table--${t.status}`} key={t.id}>
                <div className="ts-table-top">
                  <span className="ts-table-id">{t.id}</span>
                  <span className={`ts-table-dot ts-dot-${t.status}`} />
                </div>
                {t.items && <span className="ts-table-items">{t.items}</span>}
                {!t.items && <span className="ts-table-empty">Available</span>}
                <div className="ts-table-qr" aria-hidden="true">
                  <span /><span /><span />
                </div>
              </div>
            ))}
          </div>
          {/* Legend */}
          <div className="ts-legend">
            <span><i className="ts-dot-new" />New order</span>
            <span><i className="ts-dot-active" />Active</span>
            <span><i className="ts-dot-done" />Done</span>
            <span><i className="ts-dot-empty" />Available</span>
          </div>
        </div>

        {/* Side order feed */}
        <div className="ts-feed">
          <div className="ts-feed-head">
            <span className="ts-feed-dot ts-feed-dot--live" />
            <span>Live orders</span>
          </div>
          <div className="ts-ticket">
            <div className="ts-ticket-top">
              <span className="ts-tag ts-tag--new">New</span>
              <span className="ts-ticket-table">Table 7</span>
            </div>
            <div className="ts-ticket-items">
              <span>Burrata <em>×1</em></span>
              <span>Wagyu burger <em>×2</em></span>
            </div>
            <div className="ts-ticket-meta">
              <span className="ts-dot-green" />just now
            </div>
          </div>
          <div className="ts-ticket">
            <div className="ts-ticket-top">
              <span className="ts-tag ts-tag--active">Active</span>
              <span className="ts-ticket-table">Table 3</span>
            </div>
            <div className="ts-ticket-items">
              <span>Sea bass <em>×1</em></span>
              <span>House red <em>×1</em></span>
            </div>
            <div className="ts-ticket-meta">
              <span className="ts-dot-amber" />firing now
            </div>
          </div>
          <div className="ts-ticket">
            <div className="ts-ticket-top">
              <span className="ts-tag ts-tag--done">Done</span>
              <span className="ts-ticket-table">Table 11</span>
            </div>
            <div className="ts-ticket-items">
              <span>Tiramisu <em>×2</em></span>
            </div>
            <div className="ts-ticket-meta">
              <span className="ts-dot-dim" />2 min ago
            </div>
          </div>
        </div>
      </div>

      {/* Four capability callouts */}
      <div className="tableord-caps">
        {CAPABILITIES.map((c) => (
          <div className="tc-cap reveal" key={c.title}>
            <span className="tc-icon"><c.Icon /></span>
            <h4 className="display tc-title">{c.title}</h4>
            <p className="tc-body">{c.body}</p>
          </div>
        ))}
      </div>

    </section>
  );
}
